import { useCallback, useRef, useState } from 'react'
import { useTransformEffect } from 'react-zoom-pan-pinch'

import FallbackCategoryIcon from '@components/storeMap/assets/fallbackCatIcon.svg?no-inline'
import { iconMap } from '@components/storeMap/categoryGrouping/util'
import { partitionByIntersection, hackTextIntoEquallySizedLines } from '@components/storeMap/floor/2d/categoryGrouping/util'
import { useShelfCategoryGroups } from '@components/storeMap/utils'
import useDebouncedRender from '@hooks/useDebouncedRender'
import useShouldRotate from '@hooks/useShouldRotate'
import useTypedTranslation from '@hooks/useTypedTranslation'

import PinIcon from '../../../assets/pinCat.svg?no-inline'

// Base sizes in SVG coordinate units
const MARKER_SIZE = 0.9
const FONT_SIZE = 0.35
const STROKE_WIDTH = 0.16

function recalculateCollisions(list: SVGGElement[]) {
  const { withCollisions, noCollisions } = partitionByIntersection(list)

  noCollisions.forEach(g => g.style.display = 'block')
  withCollisions.forEach(g => g.style.display = 'none')
}

function Layer() {
  const { data: shelfCategoryGroups } = useShelfCategoryGroups()
  const markerRefs = useRef<SVGGElement[]>([])
  const { t } = useTypedTranslation()
  const [markerScale, setMarkerScale] = useState(1)
  const shouldRotate = useShouldRotate()

  useTransformEffect(({ state }) => {
    setMarkerScale(1 / state.scale)
  })

  const markerSize = MARKER_SIZE * markerScale
  const fontSize = FONT_SIZE * markerScale
  const strokeWidth = STROKE_WIDTH * markerScale

  // this function is called for each marker <g> element when it is mounted
  // we store the refs in an array and then filter out the intersecting ones
  // and set their display to 'none'
  const registerRef = useCallback((element: SVGGElement | null) => {
    if (!element) return
    markerRefs.current.push(element)

    recalculateCollisions(markerRefs.current)

    return () => {
      markerRefs.current = markerRefs.current.filter((ref) => ref !== element)
    }
  }, [])

  useDebouncedRender(() => recalculateCollisions(markerRefs.current), 200)

  if (!shelfCategoryGroups) return null

  return (
    <>
      {shelfCategoryGroups.map((group) => {
        const isCategoryKnown = !!iconMap[group.category]
        const iconUrl = isCategoryKnown ? iconMap[group.category] : FallbackCategoryIcon

        const y = group.center.y
        const markerX = group.center.x - markerSize / 2
        const markerY = y - markerSize / 2

        return (
          <g
            key={group.id}
            ref={registerRef}
            data-testid={`floor:category:${group.category}`}
            style={shouldRotate ? {
              transform: 'rotate(-90deg)',
              transformOrigin: 'center',
              transformBox: 'fill-box',
            } : undefined}
          >
            <MarkerText
              x={group.center.x}
              y={y + markerSize}
              fontSize={fontSize}
              strokeWidth={strokeWidth}
              text={isCategoryKnown ?
                t(`shelfCategories.${group.category}`) :
                group.category
              }
            />

            <image
              href={PinIcon}
              x={markerX}
              y={markerY}
              width={markerSize}
              height={markerSize}
              preserveAspectRatio="none"
              aria-label="catmarker-pin"
            />

            <image
              href={iconUrl}
              x={markerX + markerSize * 0.2}
              y={markerY + markerSize * 0.15}
              width={markerSize * 0.6}
              height={markerSize * 0.6}
              preserveAspectRatio="none"
            />
          </g>
        )
      })}
    </>
  )
}

interface MarkerTextProps {
  x: number
  y: number
  fontSize: number
  strokeWidth: number
  text: string
}

function MarkerText({ x, y, fontSize, strokeWidth, text }: MarkerTextProps) {
  const lines = hackTextIntoEquallySizedLines(text, 10)

  return (
    <text
      x={x}
      y={y}
      aria-label='catmarker-caption'
      fontSize={fontSize}
      fontWeight={500}
      textAnchor="middle"
      fill="var(--color-text-primary)"
      stroke="white"
      strokeWidth={strokeWidth}
      paintOrder="stroke"
    >
      {lines.map((line, idx) => (
        <tspan key={idx} x={x} dy={idx === 0 ? 0 : fontSize * 1.2}>
          {line}
        </tspan>
      ))}
    </text>
  )
}

export default Layer